'use client';
import { useMemo } from 'react';
import { CustomerKeyType } from '../../constants';
import { useWishlistCustomerKey } from '../../context';
import type { WishlistOwner } from '../../model/wishlist/wishlist';

/**
 * Resolves who the current wishlist belongs to.
 *
 * The account key wins whenever one is present — the stored key is a guest-only fallback. Identity
 * itself is resolved by the host app and handed to WishlistCustomerKeyProvider, so this stays
 * within the wishlist domain.
 *
 * `isOwnerResolved` stays false until both identity and the stored key have settled, so callers
 * never fire a request under an owner that is about to change.
 */
export const useWishlistOwner = (): WishlistOwner => {
  const { customerKey: storedCustomerKey, isKeyResolved, customerId, isCustomerIdResolved } = useWishlistCustomerKey();

  return useMemo(() => {
    if (customerId) {
      return {
        customerKey: customerId,
        customerKeyType: CustomerKeyType.CUSTOMER,
        isOwnerResolved: true,
      };
    }

    return {
      customerKey: storedCustomerKey,
      customerKeyType: CustomerKeyType.GUEST,
      isOwnerResolved: isCustomerIdResolved && isKeyResolved,
    };
  }, [customerId, storedCustomerKey, isCustomerIdResolved, isKeyResolved]);
};
